// paceCalculator.js

/**
 * @typedef {'min/km' | 'min/mile'} Unit
 */

/**
 * @typedef {Object} PaceResults
 * @property {{low: string, high: string}} easyRun
 * @property {string} threshold
 * @property {Unit} unit
 * @property {string} [vVO2max]
 * @property {string} [longRun]
 */

/**
 * Helper function to format seconds into a "M:SS" string.
 * @param {number} totalSeconds
 * @returns {string}
 */
function formatSeconds(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

/**
 * Calculate workout paces based on the 6-minute test results.
 * Easy Runs and Threshold are calculated using division by the provided divisors.
 * vVO2max is the same as the raw 6-minute test pace.
 * Long Run pace is set 15 seconds faster (i.e. lower) than the fast Easy Run pace.
 *
 * @param {number} testMinutes
 * @param {number} testSeconds
 * @param {Unit} unit
 * @returns {PaceResults}
 */
function calculatePaces(testMinutes, testSeconds, unit) {
    // Convert the test pace to total seconds
    const testPaceInSeconds = testMinutes * 60 + testSeconds;

    // Calculate paces in seconds
    const easyPaceLowSeconds = testPaceInSeconds / 0.73;
    const easyPaceHighSeconds = testPaceInSeconds / 0.66;
    const thresholdPaceSeconds = testPaceInSeconds / 0.87;

    return {
        easyRun: {
            low: formatSeconds(easyPaceLowSeconds),
            high: formatSeconds(easyPaceHighSeconds),
        },
        threshold: formatSeconds(thresholdPaceSeconds),
        unit,
        vVO2max: formatSeconds(testPaceInSeconds),
        // Long Runs: 15 seconds faster than the faster Easy Run pace
        longRun: formatSeconds(easyPaceLowSeconds - 15),
    };
}

// Export for Node / modern bundlers
module.exports = {
    calculatePaces,
};

// Example usage (for testing purposes):
if (require.main === module) {
    const testMinutes = 5;
    const testSeconds = 0;
    const unit = 'min/km';

    const paces = calculatePaces(testMinutes, testSeconds, unit);
    console.log(paces);
}
